import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Avatar,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import {
  KeyboardArrowDown as ArrowDownIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Business as WorkspaceIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useWorkspaces,
  useSelectedWorkspace,
  useWorkspaceLoading,
  useOrgStore,
  useCurrentOrg,
} from '@/store';
import { useWorkspacesHook } from '@/hooks';
import { Workspace } from '@/types';

interface WorkspaceSelectorProps {
  collapsed?: boolean;
}

const getWorkspaceColor = (id: string) => {
  const colors = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b', '#0097a7', '#5d4037', '#455a64'];
  return colors[id.charCodeAt(0) % colors.length];
};

const WorkspaceSelector: React.FC<WorkspaceSelectorProps> = ({ collapsed = false }) => {
  const navigate = useNavigate();
  const allWorkspaces = useWorkspaces();
  const selectedWorkspace = useSelectedWorkspace();
  const isLoading = useWorkspaceLoading();
  const { orgs, currentOrgId, fetchOrgs } = useOrgStore();
  const currentOrg = useCurrentOrg();
  const { fetchWorkspaces, createWorkspace, setSelectedWorkspace } = useWorkspacesHook();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceDescription, setNewWorkspaceDescription] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const hasFetched = useRef(false);
  const hasFetchedOrgs = useRef(false);

  // Filter workspaces by selected org. Show all when no org selected.
  const workspaces = currentOrgId
    ? allWorkspaces.filter(w => w.organizationId === currentOrgId)
    : allWorkspaces;

  useEffect(() => {
    if (!hasFetched.current && allWorkspaces.length === 0 && !isLoading) {
      hasFetched.current = true;
      fetchWorkspaces();
    }
  }, [fetchWorkspaces, allWorkspaces.length, isLoading]);

  useEffect(() => {
    if (!hasFetchedOrgs.current && orgs.length === 0) {
      hasFetchedOrgs.current = true;
      fetchOrgs();
    }
  }, [fetchOrgs, orgs.length]);

  // Pre-select current org when opening create dialog
  const handleOpenCreateDialog = () => {
    setAnchorEl(null);
    if (orgs.length === 0) {
      toast.warning('Please create an Organization first before creating a workspace.');
      return;
    }
    setSelectedOrgId(currentOrgId ?? orgs[0]?.organizationId ?? '');
    setCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewWorkspaceName('');
    setNewWorkspaceDescription('');
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) return;
    setIsCreating(true);
    const workspace = await createWorkspace({
      workspaceName: newWorkspaceName.trim(),
      workspaceDescription: newWorkspaceDescription.trim() || undefined,
      organizationId: selectedOrgId || undefined,
    });
    setIsCreating(false);
    if (workspace) {
      handleCloseCreateDialog();
      setSelectedWorkspace(workspace);
      navigate(`/w/${workspace.workspaceSlug}`);
    }
  };

  const handleSelectWorkspace = (workspace: Workspace) => {
    setSelectedWorkspace(workspace);
    navigate(`/w/${workspace.workspaceSlug}`);
    setAnchorEl(null);
  };

  const handleWorkspaceSettings = () => {
    if (selectedWorkspace) navigate(`/w/${selectedWorkspace.workspaceSlug}/settings`);
    setAnchorEl(null);
  };

  // Group workspaces by organization for display
  const groupedWorkspaces = React.useMemo(() => {
    if (orgs.length === 0) return [{ label: null, orgId: null, items: workspaces }];

    const groups: { label: string | null; orgId: string | null; items: Workspace[] }[] = [];

    // Group by org
    orgs.forEach(org => {
      const orgWorkspaces = workspaces.filter(w => w.organizationId === org.organizationId);
      if (orgWorkspaces.length > 0) {
        groups.push({ label: org.name, orgId: org.organizationId, items: orgWorkspaces });
      }
    });

    // Unlinked workspaces (no org)
    const unlinked = workspaces.filter(w => !w.organizationId);
    if (unlinked.length > 0) {
      groups.push({ label: null, orgId: null, items: unlinked });
    }

    // If nothing grouped, show all flat
    if (groups.length === 0) {
      return [{ label: null, orgId: null, items: workspaces }];
    }

    return groups;
  }, [workspaces, orgs]);

  if (isLoading) {
    return (
      <Box sx={{ p: 1 }}>
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: collapsed ? 0.5 : 1 }}>
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          fullWidth
          sx={{
            justifyContent: collapsed ? 'center' : 'flex-start',
            textTransform: 'none',
            py: 1,
            px: collapsed ? 1 : 2,
            borderRadius: 2,
            backgroundColor: 'action.hover',
            '&:hover': { backgroundColor: 'action.selected' },
          }}
        >
          {selectedWorkspace ? (
            <>
              <Avatar
                sx={{
                  width: 28, height: 28, fontSize: '0.875rem',
                  bgcolor: getWorkspaceColor(selectedWorkspace.workspaceId),
                  mr: collapsed ? 0 : 1.5,
                }}
              >
                {selectedWorkspace.workspaceName.charAt(0).toUpperCase()}
              </Avatar>
              {!collapsed && (
                <>
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Typography variant="body2" fontWeight={600}
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                      {selectedWorkspace.workspaceName}
                    </Typography>
                    {currentOrg && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                        {currentOrg.name}
                      </Typography>
                    )}
                  </Box>
                  <ArrowDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </>
              )}
            </>
          ) : (
            <>
              <WorkspaceIcon sx={{ mr: collapsed ? 0 : 1.5, color: 'text.secondary' }} />
              {!collapsed && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1, textAlign: 'left' }}>
                    Select Workspace
                  </Typography>
                  <ArrowDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </>
              )}
            </>
          )}
        </Button>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { minWidth: 280, maxHeight: 520 } }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {workspaces.length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No workspaces {currentOrg ? `in ${currentOrg.name}` : 'yet'}
            </Typography>
          </Box>
        ) : (
          groupedWorkspaces.map((group, gi) => (
            <Box key={group.orgId ?? `unlinked-${gi}`}>
              {/* Group header — only show when multiple orgs exist */}
              {orgs.length > 0 && (
                <Box sx={{ px: 2, pt: gi === 0 ? 1 : 0.5, pb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {group.label ?? 'Other Workspaces'}
                  </Typography>
                </Box>
              )}
              {group.items.map((workspace) => (
                <MenuItem
                  key={workspace.workspaceId}
                  onClick={() => handleSelectWorkspace(workspace)}
                  selected={selectedWorkspace?.workspaceId === workspace.workspaceId}
                  sx={{ pl: orgs.length > 0 ? 3 : 2 }}
                >
                  <ListItemIcon>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: getWorkspaceColor(workspace.workspaceId) }}>
                      {workspace.workspaceName.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={workspace.workspaceName}
                    secondary={`${workspace.projectCount || 0} projects`}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: selectedWorkspace?.workspaceId === workspace.workspaceId ? 600 : 400 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  {selectedWorkspace?.workspaceId === workspace.workspaceId && (
                    <CheckIcon fontSize="small" color="primary" />
                  )}
                </MenuItem>
              ))}
              {gi < groupedWorkspaces.length - 1 && <Divider sx={{ my: 0.5 }} />}
            </Box>
          ))
        )}

        <Divider sx={{ my: 1 }} />

        <MenuItem onClick={handleOpenCreateDialog}>
          <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Create Workspace" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>

        {selectedWorkspace && (
          <MenuItem onClick={handleWorkspaceSettings}>
            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Workspace Settings" primaryTypographyProps={{ variant: 'body2' }} />
          </MenuItem>
        )}
      </Menu>

      {/* Create Workspace Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create Workspace</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>

          {/* Block if no org */}
          {orgs.length === 0 && (
            <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" color="warning.dark" fontWeight={600}>
                Organization required
              </Typography>
              <Typography variant="body2" color="warning.dark">
                You must create an Organization before creating a workspace. Please create one from the sidebar first.
              </Typography>
            </Box>
          )}

          <TextField
            autoFocus
            label="Workspace Name"
            fullWidth
            value={newWorkspaceName}
            onChange={(e) => setNewWorkspaceName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && orgs.length > 0) handleCreateWorkspace(); }}
            disabled={orgs.length === 0}
          />

          {/* Organization selector — always shown, required */}
          <FormControl fullWidth required>
            <InputLabel>Organization</InputLabel>
            <Select
              value={selectedOrgId}
              label="Organization"
              onChange={(e: SelectChangeEvent) => setSelectedOrgId(e.target.value)}
              disabled={orgs.length === 0}
            >
              {orgs.map(org => (
                <MenuItem key={org.organizationId} value={org.organizationId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', bgcolor: 'primary.main' }}>
                      {org.name.charAt(0)}
                    </Avatar>
                    {org.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={newWorkspaceDescription}
            onChange={(e) => setNewWorkspaceDescription(e.target.value)}
            disabled={orgs.length === 0}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button
            onClick={handleCreateWorkspace}
            variant="contained"
            disabled={!newWorkspaceName.trim() || !selectedOrgId || orgs.length === 0 || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WorkspaceSelector;
